import * as styles from "./ProjectEditorDialog.css";

import { useAtomValue } from "jotai";
import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@frontend/components/atoms/Dialog";
import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import { useDiscardGuard } from "@frontend/hooks/useDiscardGuard";
import { DiscardChangesDialog } from "@frontend/shared/discard-changes-dialog";
import { Trans } from "@lingui/react/macro";

import { ClosureWeeksImpactDialog } from "./components/ClosureWeeksImpactDialog";
import { ProjectEditorBody } from "./components/ProjectEditorBody";
import { ProjectEditorHeader } from "./components/ProjectEditorHeader";
import {
  type UseProjectEditorOptions,
  useProjectEditor,
} from "./hooks/useProjectEditor";
import { useShellTitle } from "./hooks/useShellTitle";
import { projectEditorAtom } from "./stores/editor";

export type ProjectEditorDialogProps = UseProjectEditorOptions & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ProjectEditorDialog({
  open,
  onOpenChange,
  projectId = null,
  type = "intervention",
  subscriptionId = null,
  onSaved,
  onCancel,
}: ProjectEditorDialogProps): JSX.Element {
  const { project } = useAtomValue(projectEditorAtom);

  const title = useShellTitle(projectId, project?.type ?? type);

  const { loading, saving, error, conflict, dirty, impact, save, cancel } =
    useProjectEditor({
      projectId,
      type,
      subscriptionId,
      onSaved: (savedId) => {
        onOpenChange(false);
        onSaved?.(savedId);
      },
      onCancel: () => {
        onOpenChange(false);
        onCancel?.();
      },
    });

  const guard = useDiscardGuard({ dirty, onClose: cancel });

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent size="xl" className={styles.popup} aria-label={title}>
        <DialogHeader>
          <ProjectEditorHeader type={type} variant="bare" />
          <DialogClose />
        </DialogHeader>

        <ScrollArea className={styles.scrollArea}>
          <ScrollAreaViewport fade="end">
            <ScrollAreaContent
              className={styles.scroller}
              style={{ minWidth: 0 }}
            >
              <ProjectEditorBody
                loading={loading}
                error={error}
                conflict={conflict}
                stickyTabs
              />
            </ScrollAreaContent>
          </ScrollAreaViewport>

          <ScrollAreaScrollbar expandOnHover>
            <ScrollAreaThumb />
          </ScrollAreaScrollbar>
        </ScrollArea>

        <DialogFooter>
          <Button variant="neutral" onClick={guard.requestClose}>
            <Trans>Cancel</Trans>
          </Button>

          <Button
            variant="primary"
            loading={saving}
            disabled={loading}
            shortcut="mod+s"
            onClick={() => void save()}
          >
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>

        <ClosureWeeksImpactDialog {...impact} />

        <DiscardChangesDialog
          open={guard.confirming}
          onKeep={guard.keep}
          onDiscard={guard.discard}
        />
      </DialogContent>
    </Dialog>
  );
}
